import { useNavigate } from "react-router-dom";
import { Download, MessageSquarePlus, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { FileStatusBadge } from "@/components/files/FileStatusBadge";
import { api } from "@/lib/api";
import { FILE_KINDS, formatBytes, isReady } from "@/lib/files";
import { formatRelative } from "@/lib/format";

export async function downloadFile(file) {
  try {
    const { url } = await api.get(`/files/${file.id}/download`);
    window.location.assign(url);
  } catch (e) {
    toast.error(e.message);
  }
}

export function FileRow({ file, onOpen, onRename, onDelete }) {
  const navigate = useNavigate();
  const Icon = FILE_KINDS[file.kind]?.icon || FILE_KINDS.txt.icon;
  return (
    <div className="group flex items-center gap-2 rounded-xl px-3 py-3 transition-colors hover:bg-white/[0.035]" data-testid={`file-row-${file.id}`}>
      <button type="button" onClick={() => onOpen(file)} className="flex min-w-0 flex-1 items-center gap-3 text-left" data-testid={`file-row-open-${file.id}`}>
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/[0.08] bg-ink-850">
          <Icon className="h-4 w-4 text-zinc-300" strokeWidth={1.6} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[14.5px] text-zinc-100" data-testid="file-row-name">{file.name}</span>
          <span className="mt-0.5 block truncate text-[12px] text-zinc-500">
            {FILE_KINDS[file.kind]?.label} · {formatBytes(file.size_bytes)} · {formatRelative(file.created_at)}
          </span>
        </span>
        <FileStatusBadge file={file} className="shrink-0" />
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" aria-label="File actions" data-testid={`file-row-menu-${file.id}`} className="rounded-md p-1.5 text-zinc-500 hover:bg-white/[0.06] hover:text-white md:opacity-0 md:group-hover:opacity-100 data-[state=open]:opacity-100">
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="border-white/10 bg-ink-850">
          {isReady(file) && (
            <DropdownMenuItem onSelect={() => navigate("/", { state: { attach: [file] } })} className="gap-2 focus:bg-white/[0.06]" data-testid={`file-row-ask-${file.id}`}>
              <MessageSquarePlus className="h-4 w-4" /> Ask ChatSleek
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={() => downloadFile(file)} className="gap-2 focus:bg-white/[0.06]" data-testid={`file-row-download-${file.id}`}>
            <Download className="h-4 w-4" /> Download
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => onRename(file)} className="gap-2 focus:bg-white/[0.06]" data-testid={`file-row-rename-${file.id}`}>
            <Pencil className="h-4 w-4" /> Rename
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => onDelete(file)} className="gap-2 text-rose-300 focus:bg-rose-500/10 focus:text-rose-200" data-testid={`file-row-delete-${file.id}`}>
            <Trash2 className="h-4 w-4" /> Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
